import { Component, Input, OnChanges } from '@angular/core';

import { ReviewStats } from '../../interfaces/review-stats.interface';
import {
  CountVariation, formatCount, formatDecimal, formatSharePercent, rateVariation, share
} from '../../utils/review-stats-format.util';

/** Uma barra da distribuição: rótulo, valor, largura relativa e a classe que a pinta. */
interface DistributionBar {
  key: string;
  label: string;
  value: number;
  /** A quantidade formatada, com milhar: "3.691". */
  count: string;
  share: string;
  /** Largura na leitura por quantidade: relativa ao maior valor. */
  width: number;
  /** Largura na leitura por percentual: o próprio percentual. */
  shareWidth: number;
  modifier: string;
}

/**
 * O que a barra mostra. Número e percentual juntos ("3691 39%") liam como um valor só; um
 * de cada vez, com a barra medindo a mesma coisa que o texto.
 */
export type DistributionMode = 'count' | 'share';

/**
 * Severidade dos achados e veredito dos arquivos — os dois cards que só existem depois
 * das colunas gravadas na conclusão do review.
 *
 * Barras em CSS, e não `po-chart`: são três (ou quatro) fatias de uma distribuição
 * conhecida, cada uma com cor semântica fixa, e o gráfico do PO não deixa pintar fatia
 * por chave. Um gráfico aqui custaria a cor que é justamente o que se quer ler.
 */
@Component({
  selector: 'app-review-stats-severity',
  templateUrl: './review-stats-severity.component.html',
  styleUrls: ['./review-stats-severity.component.css']
})
export class ReviewStatsSeverityComponent implements OnChanges {
  @Input() stats: ReviewStats | null = null;

  /** A janela anterior, para a variação da incidência de ALTA. `null` sem comparação. */
  @Input() previous: ReviewStats | null = null;

  /**
   * `true` mostra "quantos REVIEWS têm ao menos um achado desta severidade"; `false`,
   * "quantos ACHADOS são desta severidade". São perguntas diferentes sobre os mesmos
   * dados, e a segunda é multi-valorada — daí o alternador, em vez de um card escolher
   * por conta própria e o leitor não saber qual está vendo.
   */
  byReview = false;
  /** Leitura de cada card — independentes: comparar severidades e vereditos pede escalas diferentes. */
  severityMode: DistributionMode = 'share';
  verdictMode: DistributionMode = 'share';

  severityBars: DistributionBar[] = [];
  verdictBars: DistributionBar[] = [];

  readonly cardHelp = {
    severity:
      'Gravidade das correções necessárias apontadas no período.\n\n' +
      '• Por correção: quantas correções são de cada gravidade.\n' +
      '• Por review: quantos reviews têm ao menos um achado daquela severidade. Um review ' +
      'com ALTA e BAIXA conta nos dois, então a soma passa do total de reviews.\n' +
      '• Melhorias sugeridas não têm gravidade — elas têm categoria e ficam em outro bloco.\n' +
      '• Correções necessárias: total de ações obrigatórias no período — pode passar da soma ' +
      'das três barras acima quando há review antigo sem severidade detalhada.\n' +
      '• Vulnerabilidades: achados marcados como falha de segurança (classificação do Sonar). ' +
      'Não é uma quarta gravidade — atravessa as duas listas, tanto correção obrigatória ' +
      'quanto sugestão, então não é um subconjunto exato das Correções necessárias.',
    verdict:
      'A conclusão do assistente sobre cada ARQUIVO revisado — há um resultado por arquivo, ' +
      'e um review costuma ter mais de um.\n\n' +
      '• Bloqueados: reviews com ao menos um arquivo rejeitado. Esta linha conta reviews, ' +
      'não arquivos.\n' +
      '• Sem resultado válido: o assistente devolveu formato inválido. É falha da ' +
      'ferramenta, não juízo sobre o código — por isso fica fora das barras e da nota média.\n' +
      '• Sem detalhamento: reviews anteriores a este controle, ainda sem veredito gravado.'
  };

  private static readonly SEVERITY_LABELS: Record<string, string> = {
    ALTA: 'Alta', MEDIA: 'Média', BAIXA: 'Baixa'
  };

  private static readonly VERDICT_LABELS: Record<string, string> = {
    APROVADO: 'Aprovado',
    AJUSTES_NECESSARIOS: 'Ajustes necessários',
    REJEITADO: 'Rejeitado'
  };

  ngOnChanges(): void {
    this.build();
  }

  /**
   * Recalculado no evento, e não num getter lido pelo template: um getter que monta
   * array devolve referência nova a cada ciclo de change detection — a armadilha
   * documentada em `costs-panel.component.ts`.
   */
  onScopeChange(byReview: boolean): void {
    this.byReview = byReview;
    this.build();
  }

  private build(): void {
    const findings = this.stats?.findings;
    const source = this.byReview ? findings?.reviewsBySeverity : findings?.bySeverity;
    this.severityBars = this.toBars(
      source,
      ReviewStatsSeverityComponent.SEVERITY_LABELS,
      { ALTA: 'high', MEDIA: 'medium', BAIXA: 'low' },
      this.byReview ? this.stats?.totals?.total : undefined
    );

    this.verdictBars = this.toBars(this.stats?.quality?.byVerdict, ReviewStatsSeverityComponent.VERDICT_LABELS, {
      APROVADO: 'approved', AJUSTES_NECESSARIOS: 'changes', REJEITADO: 'rejected'
    });
  }

  /** O texto ao lado da barra, na leitura escolhida. */
  barText(bar: DistributionBar, mode: DistributionMode): string {
    return mode === 'count' ? bar.count : bar.share;
  }

  /** A largura da barra, na leitura escolhida. */
  barWidth(bar: DistributionBar, mode: DistributionMode): number {
    return mode === 'count' ? bar.width : bar.shareWidth;
  }

  private toBars(
    source: Record<string, number> | undefined,
    labels: Record<string, string>,
    modifiers: Record<string, string>,
    denominator?: number
  ): DistributionBar[] {
    if (!source) { return []; }

    const keys = Object.keys(labels).filter(key => key in source);
    const total = denominator ?? keys.reduce((sum, key) => sum + (source[key] ?? 0), 0);
    // A largura é relativa ao MAIOR valor, não ao total: com 3 de 800 achados em ALTA,
    // uma barra proporcional ao total seria invisível justamente na fatia que importa.
    // Vale para a leitura por quantidade; na por percentual a barra é a proporção real.
    const max = keys.reduce((peak, key) => Math.max(peak, source[key] ?? 0), 0);

    return keys.map(key => {
      const value = source[key] ?? 0;
      return {
        key,
        label: labels[key],
        value,
        count: formatCount(value),
        share: total ? formatSharePercent(value, total) : '',
        // Na leitura por percentual a barra É o percentual: com a largura relativa ao
        // maior, uma fatia de 43% aparecia cheia ao lado do texto "43%".
        shareWidth: value > 0 && total ? Math.max(2, Math.round((value / total) * 100)) : 0,
        // O piso de 2% vale só para valor MAIOR que zero: ele existe para que uma fatia
        // pequena continue visível, e aplicá-lo ao zero desenharia uma barra para uma
        // severidade que não aconteceu.
        width: value > 0 && max ? Math.max(2, Math.round((value / max) * 100)) : 0,
        modifier: modifiers[key] ?? ''
      };
    });
  }

  // ─── Severidade ──────────────────────────────────────────────────────────

  /** Sem nenhuma ação obrigatória detalhada não há distribuição a mostrar. */
  get hasSeverity(): boolean {
    return this.severityBars.some(bar => bar.value > 0);
  }

  get highLabel(): string {
    return formatCount(this.stats?.findings?.bySeverity?.['ALTA']);
  }

  get highReviewRateLabel(): string {
    const reviews = this.stats?.findings?.reviewsBySeverity?.['ALTA'];
    const total = this.stats?.findings?.detailedReviews;
    if (reviews == null || !total) { return '—'; }
    return formatSharePercent(reviews, total);
  }

  /** Variação em pontos percentuais da incidência de ALTA contra o período anterior. */
  get highChange(): CountVariation | null {
    const current = this.stats?.findings;
    const previous = this.previous?.findings;
    return rateVariation(
      share(current?.reviewsBySeverity?.['ALTA'] ?? 0, current?.detailedReviews ?? 0),
      share(previous?.reviewsBySeverity?.['ALTA'] ?? 0, previous?.detailedReviews ?? 0)
    );
  }

  get reviewsWithHighLabel(): string {
    const reviews = this.stats?.findings?.reviewsBySeverity?.['ALTA'];
    const total = this.stats?.findings?.detailedReviews;
    if (reviews == null || !total) { return '—'; }
    return `${formatCount(reviews)} de ${formatCount(total)} (${formatSharePercent(reviews, total)})`;
  }

  get suggestionsLabel(): string {
    return formatCount(this.stats?.findings?.suggestions);
  }

  get requiredActionsLabel(): string {
    return formatCount(this.stats?.findings?.requiredActions);
  }

  get hasVulnerabilities(): boolean {
    return (this.stats?.findings?.vulnerabilities ?? 0) > 0;
  }

  get vulnerabilitiesLabel(): string {
    return formatCount(this.stats?.findings?.vulnerabilities);
  }

  // ─── Veredito ────────────────────────────────────────────────────────────

  get hasVerdict(): boolean {
    return this.verdictBars.some(bar => bar.value > 0);
  }

  get scoreLabel(): string {
    return formatDecimal(this.stats?.quality?.scoreAverage);
  }

  get blockedLabel(): string {
    const quality = this.stats?.quality;
    const total = this.stats?.totals?.total;
    if (!quality || !total) { return '—'; }
    return `${formatCount(quality.reviewsWithBlockingFile)} de ${formatCount(total)}`;
  }

  get hasBlocked(): boolean {
    return (this.stats?.quality?.reviewsWithBlockingFile ?? 0) > 0;
  }

  get hasInvalidAnswers(): boolean {
    return (this.stats?.quality?.invalidAnswers ?? 0) > 0;
  }

  /**
   * A taxa de resposta inválida sai sobre os arquivos com veredito conhecido — a mesma
   * base de `byVerdict`. É um número pequeno por natureza (1% em produção), e mostrá-lo
   * sem a proporção faria "10" parecer grande ou pequeno conforme o período.
   */
  get invalidAnswersLabel(): string {
    const quality = this.stats?.quality;
    if (!quality?.files) { return formatCount(quality?.invalidAnswers); }
    return `${formatCount(quality.invalidAnswers)} (${formatSharePercent(quality.invalidAnswers, quality.files)})`;
  }

  /** Arquivos ainda sem veredito — reviews anteriores às colunas. Nunca "aprovados". */
  get hasUnknownFiles(): boolean {
    return (this.stats?.quality?.unknownFiles ?? 0) > 0;
  }

  get unknownFilesLabel(): string {
    const quality = this.stats?.quality;
    if (!quality) { return '—'; }
    const base = quality.files + quality.unknownFiles;
    return `${formatCount(quality.unknownFiles)} (${formatSharePercent(quality.unknownFiles, base)})`;
  }
}
